import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Shield, Phone, Clock, Heart, Star, ChevronDown, CheckCircle, Users, ArrowRight } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_findmed-app/artifacts/ge9othd8_LOGO%20SOS%20FINDMED.png";
const IMG_AMBULANCE = "https://static.prod-images.emergentagent.com/jobs/5a05bedb-be0f-43c6-9283-31cf11d22107/images/d0067b7b44b5576d5499c1c44606e9d954c2cee8167dcc77d0d8a65d54683c8f.png";
const IMG_FAMILY = "https://images.unsplash.com/photo-1577752698433-8c4abc136df7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxoYXBweSUyMG1leGljYW4lMjBmYW1pbHklMjBwcm90ZWN0aW9uJTIwc2FmZXR5fGVufDB8fHx8MTc3NTM3NzI1M3ww&ixlib=rb-4.1.0&q=85";
const IMG_EMERGENCY = "https://static.prod-images.emergentagent.com/jobs/5a05bedb-be0f-43c6-9283-31cf11d22107/images/e84e8fcb2921d96954cb445dd57339a52b2539ec392ca2b42403fec565330350.png";

const TESTIMONIALS = [
  { name: "María G.", city: "Querétaro Centro", text: "Nunca pensé necesitarlo hasta que mi esposo tuvo una emergencia. La ambulancia llegó en minutos. Vale cada peso.", rating: 5 },
  { name: "Roberto S.", city: "Juriquilla", text: "Lo compré para mis papás. La tranquilidad de saber que están protegidos no tiene precio. Son $899 al año, menos de $75 al mes.", rating: 5 },
  { name: "Ana L.", city: "El Marqués", text: "Mi hija tuvo un accidente y con una llamada llegó la ambulancia. Sin SOS FindMed no sé qué habría pasado.", rating: 5 }
];

export default function CampaignLanding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref') || '';
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [faqOpen, setFaqOpen] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % TESTIMONIALS.length);
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCTA = () => {
    navigate(ref ? `/register?ref=${ref}` : '/register');
  };

  const faqs = [
    { q: "¿Qué incluye la membresía?", a: "Un servicio de ambulancia de emergencia durante un año completo. Incluye traslado al hospital más cercano, atención paramédica en el trayecto y tarjeta digital con tu información médica." },
    { q: "¿Cuánto cuesta?", a: "Solo $899 MXN al año. Eso es menos de $2.50 al día por la tranquilidad de tener una ambulancia cuando más la necesitas." },
    { q: "¿Cuándo puedo usar el servicio?", a: "Tu membresía se activa 72 horas después de tu compra. A partir de ahí, puedes llamar al 55 2567 1884 en cualquier emergencia médica." },
    { q: "¿Es solo para mí?", a: "La membresía es personal e intransferible, vinculada a tu INE. Pero puedes comprar membresías adicionales para tus familiares a precio completo." },
    { q: "¿Qué pasa cuando uso el servicio?", a: "Una vez que utilizas tu servicio de ambulancia, tu membresía se marca como consumida. Te notificaremos para que puedas renovar y seguir protegido." },
    { q: "¿Cubre toda la ciudad de Querétaro?", a: "Sí, cubrimos toda la zona metropolitana de Querétaro incluyendo El Marqués, Corregidora y Juriquilla." }
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white" data-testid="campaign-landing">

      {/* ===== HERO ===== */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG_AMBULANCE} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-transparent to-transparent" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-20">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <img src={LOGO_URL} alt="SOS FindMed" className="w-40 h-auto" style={{ filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.9)) drop-shadow(0 0 15px rgba(255,255,255,0.4))' }} />
              <div>
                <p className="text-white/60 text-xs tracking-[4px] uppercase">Querétaro</p>
                <p className="text-white text-xl font-bold tracking-wider">SOS FindMed</p>
              </div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.1] mb-6">
              Una ambulancia<br />
              <span style={{ color: '#C81D25' }}>cuando más</span><br />
              la necesitas
            </h1>
            <p className="text-lg sm:text-xl text-white/70 mb-4 max-w-lg leading-relaxed">
              Servicio de ambulancia prepagada para emergencias reales. 
              Tu protección personal por solo <strong className="text-white">$2.46 al día</strong>.
            </p>
            <div className="flex items-center gap-3 mb-8">
              <div className="flex -space-x-2">
                {[1,2,3,4].map(i => (
                  <div key={i} className="w-8 h-8 rounded-full border-2 border-[#0D0D0D]" style={{ background: `hsl(${i*60}, 50%, 60%)` }} />
                ))}
              </div>
              <p className="text-white/50 text-sm">Familias en Querétaro ya están protegidas</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={handleCTA}
                className="bg-[#C81D25] hover:bg-[#A6141B] text-white px-10 py-7 text-lg font-bold rounded-full shadow-2xl shadow-red-900/40 transition-all hover:scale-105"
                data-testid="hero-cta-btn">
                Actívate Hoy — $899/año
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <a href="tel:5525671884">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 px-8 py-7 text-lg rounded-full">
                  <Phone className="w-5 h-5 mr-2" /> Llámanos
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== URGENCY STRIP ===== */}
      <section className="bg-[#C81D25] py-4">
        <div className="max-w-6xl mx-auto px-6 flex flex-wrap items-center justify-center gap-6 sm:gap-12 text-center">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-white/80" />
            <span className="text-white font-semibold text-sm">Respuesta inmediata 24/7</span>
          </div>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-white/80" />
            <span className="text-white font-semibold text-sm">Cobertura toda zona metro QRO</span>
          </div>
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-white/80" />
            <span className="text-white font-semibold text-sm">Paramédicos certificados</span>
          </div>
        </div>
      </section>

      {/* ===== PROBLEM / SOLUTION ===== */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C81D25] text-sm font-bold tracking-[4px] uppercase mb-3">La realidad</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
              ¿Sabías que una ambulancia privada<br className="hidden sm:block" /> cuesta entre $8,000 y $25,000 MXN?
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              Y las públicas pueden tardar más de 45 minutos en llegar. En una emergencia real, cada segundo cuenta.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="relative rounded-2xl overflow-hidden">
              <img src={IMG_EMERGENCY} alt="Emergencia" className="w-full h-80 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-white text-xl font-bold">Cuando pasa lo inesperado...</p>
                <p className="text-white/70 text-sm mt-1">No es momento de pensar en costos</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="flex gap-4 p-5 rounded-xl bg-white/5 border border-white/10">
                <div className="w-10 h-10 rounded-lg bg-[#C81D25]/20 flex items-center justify-center shrink-0">
                  <span className="text-[#C81D25] font-bold text-lg">1</span>
                </div>
                <div>
                  <p className="text-white font-semibold mb-1">Compra tu membresía</p>
                  <p className="text-white/50 text-sm">$899 MXN al año. Regístrate en 3 minutos con tu INE.</p>
                </div>
              </div>
              <div className="flex gap-4 p-5 rounded-xl bg-white/5 border border-white/10">
                <div className="w-10 h-10 rounded-lg bg-[#FFD700]/20 flex items-center justify-center shrink-0">
                  <span className="text-[#FFD700] font-bold text-lg">2</span>
                </div>
                <div>
                  <p className="text-white font-semibold mb-1">Recibe tu tarjeta digital</p>
                  <p className="text-white/50 text-sm">Con tu número de membresía y línea de emergencias. Guárdala en tu celular.</p>
                </div>
              </div>
              <div className="flex gap-4 p-5 rounded-xl bg-white/5 border border-white/10">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center shrink-0">
                  <span className="text-emerald-400 font-bold text-lg">3</span>
                </div>
                <div>
                  <p className="text-white font-semibold mb-1">Llama cuando lo necesites</p>
                  <p className="text-white/50 text-sm">Marca al 55 2567 1884 y una ambulancia sale inmediatamente hacia ti.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PRICE ANCHOR ===== */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#0D0D0D] to-[#111]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#FFD700] text-sm font-bold tracking-[4px] uppercase mb-3">Inversión</p>
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-8">
            Menos que un café al día
          </h2>
          <div className="bg-white/5 border border-white/10 rounded-3xl p-8 sm:p-12 max-w-lg mx-auto">
            <div className="flex items-center justify-center gap-4 mb-6">
              <span className="text-white/30 text-xl line-through">$8,000+ ambulancia privada</span>
            </div>
            <div className="mb-6">
              <span className="text-6xl sm:text-7xl font-black text-white">$899</span>
              <span className="text-2xl text-white/50 ml-2">MXN/año</span>
            </div>
            <p className="text-[#FFD700] text-lg font-semibold mb-8">= $2.46 al día = $75 al mes</p>
            <ul className="text-left space-y-3 mb-8">
              {["Ambulancia de emergencia 24/7", "Cobertura zona metropolitana QRO", "Paramédicos certificados", "Tarjeta digital personalizada", "Atención paramédica en trayecto", "Traslado al hospital más cercano"].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-white/80">
                  <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <Button onClick={handleCTA}
              className="w-full bg-[#C81D25] hover:bg-[#A6141B] text-white py-7 text-lg font-bold rounded-full shadow-2xl shadow-red-900/40 transition-all hover:scale-105"
              data-testid="price-cta-btn">
              Quiero Mi Protección
            </Button>
            <p className="text-white/30 text-xs mt-4">Membresía anual. Se activa 72h después de la compra.</p>
          </div>
        </div>
      </section>

      {/* ===== FAMILY SECTION ===== */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[#C81D25] text-sm font-bold tracking-[4px] uppercase mb-3">Para tu familia</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-6 leading-tight">
              La mejor inversión es la tranquilidad de los tuyos
            </h2>
            <p className="text-white/60 text-lg mb-6 leading-relaxed">
              Imagina que tu mamá, tu papá o tus hijos tienen una emergencia y tú no estás cerca. 
              Con SOS FindMed, una sola llamada activa todo un equipo de paramédicos.
            </p>
            <p className="text-white/60 text-lg mb-8 leading-relaxed">
              <strong className="text-white">Regala protección.</strong> Compra una membresía individual para quien más quieres.
              Cada membresía es personal por $899 MXN/año.
            </p>
            <Button onClick={handleCTA}
              className="bg-[#FFD700] hover:bg-[#FFA500] text-black px-8 py-6 text-base font-bold rounded-full transition-all hover:scale-105"
              data-testid="family-cta-btn">
              Comprar para un Familiar
            </Button>
          </div>
          <div className="relative rounded-2xl overflow-hidden">
            <img src={IMG_FAMILY} alt="Familia protegida" className="w-full h-96 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-20 px-6 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#FFD700] text-sm font-bold tracking-[4px] uppercase mb-3">Testimonios</p>
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-12">
            Lo que dicen nuestros miembros
          </h2>
          <div className="relative">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className={`transition-all duration-500 ${i === activeTestimonial ? 'opacity-100' : 'opacity-0 absolute inset-0'}`}>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-8 max-w-2xl mx-auto">
                  <div className="flex justify-center gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <Star key={j} className="w-5 h-5 fill-[#FFD700] text-[#FFD700]" />
                    ))}
                  </div>
                  <p className="text-white/80 text-lg italic mb-6 leading-relaxed">"{t.text}"</p>
                  <p className="text-white font-semibold">{t.name}</p>
                  <p className="text-white/40 text-sm">{t.city}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-2 mt-6">
            {TESTIMONIALS.map((_, i) => (
              <button key={i} onClick={() => setActiveTestimonial(i)}
                className={`w-2 h-2 rounded-full transition-all ${i === activeTestimonial ? 'bg-[#C81D25] w-6' : 'bg-white/20'}`} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#C81D25] text-sm font-bold tracking-[4px] uppercase mb-3">Preguntas frecuentes</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white">Resolvemos tus dudas</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                <button onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left" data-testid={`faq-${i}`}>
                  <span className="text-white font-medium pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-white/40 shrink-0 transition-transform ${faqOpen === i ? 'rotate-180' : ''}`} />
                </button>
                {faqOpen === i && (
                  <div className="px-5 pb-5 -mt-1">
                    <p className="text-white/60 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-20 px-6 bg-gradient-to-t from-[#C81D25]/20 to-transparent">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
            No esperes a que sea tarde
          </h2>
          <p className="text-white/60 text-lg mb-8 max-w-xl mx-auto">
            Nadie planea una emergencia. Pero quienes se preparan, hacen la diferencia cuando más importa.
          </p>
          <Button onClick={handleCTA}
            className="bg-[#C81D25] hover:bg-[#A6141B] text-white px-12 py-7 text-xl font-bold rounded-full shadow-2xl shadow-red-900/40 transition-all hover:scale-105"
            data-testid="final-cta-btn">
            Activar Mi Protección — $899/año
          </Button>
          <div className="mt-8 flex items-center justify-center gap-6 text-white/40 text-sm">
            <span className="flex items-center gap-2"><Shield className="w-4 h-4" /> Pago seguro</span>
            <span className="flex items-center gap-2"><Clock className="w-4 h-4" /> Activo en 72h</span>
            <span className="flex items-center gap-2"><Users className="w-4 h-4" /> +50 familias</span>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="py-10 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="SOS FindMed" className="w-16 h-auto" style={{ filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.8))' }} />
            <span className="text-white/50 text-sm">SOS FindMed - Querétaro</span>
          </div>
          <div className="flex items-center gap-4 text-white/40 text-sm">
            <a href="/terminos" className="hover:text-white/70">Términos</a>
            <a href="/privacidad" className="hover:text-white/70">Privacidad</a>
            <a href="tel:5525671884" className="hover:text-white/70">55 2567 1884</a>
          </div>
          <p className="text-white/30 text-xs">© 2026 SOS FindMed</p>
        </div>
      </footer>
    </div>
  );
}
